import { FC, ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';

/**
 * Variant types for ActionDialog
 * - confirm: Standard confirmation dialog (primary color)
 * - danger: Destructive action dialog (error color)
 * - form: Dialog with custom form content (primary color)
 */
export type ActionDialogVariant = 'confirm' | 'danger' | 'form';

export interface ActionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Dialog content - string for simple text, ReactNode for form content */
  content?: string | ReactNode;
  /** Text for confirm button */
  confirmText?: string;
  /** Text for cancel button */
  cancelText?: string;
  /** Handler for confirm action */
  onConfirm: () => void;
  /** Handler for cancel/close action */
  onCancel: () => void;
  /** Whether the dialog is in loading state */
  loading?: boolean;
  /** Dialog variant - affects confirm button color */
  variant?: ActionDialogVariant;
  /** Maximum width of the dialog */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Whether confirm button should be disabled (in addition to loading state) */
  confirmDisabled?: boolean;
  /** Custom form content to render below the description */
  children?: ReactNode;
  /** Accessibility label ID for the dialog */
  ariaLabelledBy?: string;
  /** Accessibility description ID for the dialog */
  ariaDescribedBy?: string;
}

/**
 * A reusable dialog component for confirmations, dangerous actions, and forms.
 *
 * @example
 * // Simple confirmation dialog
 * <ActionDialog
 *   open={isOpen}
 *   title="Confirm Action"
 *   content="Are you sure you want to proceed?"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 * />
 *
 * @example
 * // Danger dialog for deletion
 * <ActionDialog
 *   open={isOpen}
 *   title="Delete Item"
 *   content="This action cannot be undone."
 *   variant="danger"
 *   confirmText="Delete"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 *   loading={isDeleting}
 * />
 *
 * @example
 * // Form dialog with custom content
 * <ActionDialog
 *   open={isOpen}
 *   title="Edit Item"
 *   content="Update the item details below."
 *   variant="form"
 *   confirmText="Save"
 *   onConfirm={handleSave}
 *   onCancel={handleCancel}
 *   loading={isSaving}
 * >
 *   <TextField value={name} onChange={handleChange} />
 * </ActionDialog>
 */
const ActionDialog: FC<ActionDialogProps> = ({
  open,
  title,
  content,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
  variant = 'confirm',
  maxWidth = 'xs',
  confirmDisabled = false,
  children,
  ariaLabelledBy,
  ariaDescribedBy,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Determine button color based on variant
  const confirmButtonColor = variant === 'danger' ? 'error' : 'primary';

  // Generate default aria IDs if not provided
  const dialogTitleId = ariaLabelledBy || `action-dialog-title-${title.toLowerCase().replace(/\s+/g, '-')}`;
  const dialogDescriptionId =
    ariaDescribedBy || `action-dialog-description-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // Handle close - disabled when loading
  const handleClose = () => {
    if (!loading) {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={dialogTitleId}
      aria-describedby={content ? dialogDescriptionId : undefined}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: isMobile ? { margin: 2 } : {},
      }}
    >
      <DialogTitle id={dialogTitleId}>{title}</DialogTitle>
      <DialogContent>
        {typeof content === 'string' ? (
          <DialogContentText id={dialogDescriptionId} sx={children ? { mb: 2 } : undefined}>
            {content}
          </DialogContentText>
        ) : content ? (
          <>{content}</>
        ) : null}
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary" disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmButtonColor}
          variant="contained"
          disabled={loading || confirmDisabled}
        >
          {loading ? <CircularProgress size={24} /> : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionDialog;
